// React
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeCanvas } from "qrcode.react";
import { queryMerchantDetail, updateAllinPayKey } from "../model";
const config = require(
  `../../../../../config/${process.env.REACT_APP_ENV}/env.json`,
);
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { FormBuilder } from "components/FormBuilder/FormBuilder";
import { useWatch } from "react-hook-form";
import ButtonMAS from "ButtonMAS/ButtonMAS";

const PAGE_LOCALE_KEY = "page.employees.";
const FORM_LABEL_KEY = PAGE_LOCALE_KEY + "employeeDetail.detail.fields";
const EMPLOYEE_LOCALE_KEY = "page.employees.";

/**
 * @typedef {import("../OfflineEmployeeDetail").offlineEmployeeDetailPartitionKeyType} offlineEmployeeDetailPartitionKeyType
 * 
 * @param {Object} props
 * @param {import("utils/usePageWrapper/usePageWrapper").CallApisFn<offlineEmployeeDetailPartitionKeyType | string>} props.callApis 
 * @returns 
 */
const EmployeeSettlement = ({
  callApis,
  formProps,
  isEdit,
  isLoading,
  employee,
  isOtherProcessor,
  isCreate = false,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [isRenewingKey, setIsRenewingKey] = useState(false);
  const [isDownloadingQrcode, setIsDownloadingQrcode] = useState(false);

  const isDisabled = !isEdit || isLoading;
  const isAutosettlement = useWatch({ control: formProps.control, name: "isAutosettlement" });

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const handleDownloadQrcode = async () => {
    setIsDownloadingQrcode(true);
    // Fetch merchant info (used for text rendering)
    let merchantName = "";
    let merchantId = employee?.merchantId ?? "";
    try {
      const [merchantResp] = await callApis([{
        api: queryMerchantDetail,
        payload: employee.merchantId,
      }])

      if (!merchantResp.isSuccess && merchantResp.isError) {
        snackbar(
          `${t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.fetchMerchantDetailFail")}`,
          "error",
        );
        return;
      }

      const merchant = merchantResp?.data;
      merchantName = merchant?.name || "";
      merchantId = merchantId || merchant?.id || employee?.merchantId || "";
    } catch (err) {
      console.error(err);
    }

    // Get the already-rendered QR canvas
    const srcCanvas = document.getElementById("static-qrcode");
    if (!srcCanvas) return;

    // Ensure fonts are ready (if supported), so measurements are accurate
    if (document.fonts?.ready) {
      try {
        await document.fonts.ready;
      } catch (err) {
        console.error(err);
      }
    }

    const qrWidth = srcCanvas.width; // intrinsic bitmap width
    const qrHeight = srcCanvas.height; // intrinsic bitmap height

    // Add padding around the QR image
    const qrPad = Math.round(Math.max(qrWidth, qrHeight) * 0.08); // ~8% padding
    const qrPadX = qrPad;
    const qrPadY = qrPad;

    // Layout config (proportional to QR size for crisp output)
    const paddingY = Math.round(qrHeight * 0.1); // vertical padding above/below text block
    const lineGap = Math.round(qrHeight * 0.06);
    const maxTextWidth = Math.round((qrWidth + qrPadX * 2) * 0.9);

    // Prepare canvas for final image: QR on top + two lines of text below
    // We'll compute font sizes that fit within maxTextWidth
    const ctxForMeasure = document.createElement("canvas").getContext("2d");
    const pickFont = (basePct, text, weight = "400") => {
      let size = Math.max(12, Math.round(qrWidth * basePct));
      // Try preferred font stack, fall back to system
      const family =
        "Montserrat, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif";
      while (size > 10) {
        ctxForMeasure.font = `${weight} ${size}px ${family}`;
        const w = ctxForMeasure.measureText(text).width;
        if (w <= maxTextWidth) break;
        size -= 1;
      }
      return { size, family, weight };
    };

    const nameText = merchantName || "";
    const idText = `${String(merchantId).slice?.(-2)}${String(
      employee.employeeId,
    ).slice?.(-2)}`;

    const nameFont = pickFont(0.12, nameText, "700");
    const idFont = pickFont(0.07, idText || " ", "500");

    const textBlockHeight =
      paddingY +
      (idText ? idFont.size + lineGap : 0) +
      nameFont.size +
      paddingY;

    const outCanvas = document.createElement("canvas");
    outCanvas.width = qrWidth + qrPadX * 2;
    outCanvas.height = qrHeight + qrPadY * 2 + textBlockHeight;
    const ctx = outCanvas.getContext("2d");

    // White background
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, outCanvas.width, outCanvas.height);

    // Draw QR at top with padding
    ctx.drawImage(srcCanvas, qrPadX, qrPadY, qrWidth, qrHeight);

    // Draw texts centered
    ctx.textAlign = "center";
    ctx.textBaseline = "top";

    let y = qrPadY + qrHeight + paddingY;
    const centerX = outCanvas.width / 2;

    // ID above Name
    if (idText) {
      ctx.font = `${idFont.weight} ${idFont.size}px ${idFont.family}`;
      ctx.fillStyle = "#333333";
      ctx.fillText(idText, centerX, y);
      y += idFont.size + lineGap;
    }

    if (nameText) {
      ctx.font = `${nameFont.weight} ${nameFont.size}px ${nameFont.family}`;
      ctx.fillStyle = "#111111";
      ctx.fillText(nameText, centerX, y);
      y += nameFont.size;
    }

    // Export and download
    const dataUrl = outCanvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `qrcode-${employee?.uuid || "qr"}-${Date.now()}.png`;
    a.click();
    setIsDownloadingQrcode(false);
  };

  const handleRenewAllinPayKey = async () => {
    try {
      setIsRenewingKey(true); // Set loading state for this button only
      const [updateAllinPayKeyResp] = await callApis([{
        api: updateAllinPayKey,
        payload: employee.employeeId,
        blockOnError: false,
        blockOnUnauthorized: false,
      }]);
      if (!updateAllinPayKeyResp.isSuccess) {
        throw new Error(
          updateAllinPayKeyResp?.data?.message || "Update failed",
        );
      } else {
        snackbar(
          `${t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.updateSuccess")}`,
          "success",
        );
      }
    } catch (error) {
      console.error("Renew key error:", error);
      snackbar(
        `${t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.updateFailed")}: ${error.message}`,
        "error",
      );
    } finally {
      setIsRenewingKey(false); // Reset loading state
    }
  };

  /** @type {Record<string, import("components/FormBuilder/lib/FormBuilderFieldBuilder").formBuilderFieldConfig>} */
  const fieldsConfig = {
    isAutoSettlement: {
      fieldType: "checkbox",
      fieldName: "isAutosettlement",
      label: t(`${FORM_LABEL_KEY}.isAutoSettlement`),
      disabled: isDisabled,
      config: {
        defaultValue: true,
        defaultValueTriggerUpdate: true,
        customOnCheckFn: (value, { setValue }) => {
          if (value)
            setValue("settlementDate", dayjs().endOf("day").set("ms", 0).toDate());
          else setValue("settlementDate", "");
        }
      }
    },
    settlementDate: {
      fieldType: "timePicker",
      fieldName: "settlementDate",
      label: t(`${FORM_LABEL_KEY}.settlementDate`),
      mandatory: isAutosettlement,
      disabled: isDisabled || !isAutosettlement,
      config: {
        defaultValue: dayjs().endOf("day").set("ms", 0).toDate(),
        rules: isAutosettlement ? ["validDate"] : [],
      },
    },
    qrCodeUrl: !isCreate && {
      fieldType: "textDisplay",
      label: "URL",
      config: {
        fontVariant: "body1",
        displayValue: `${config.STATIC_QRCODE}/${employee.uuid}`,
        href: `${config.STATIC_QRCODE}/${employee.uuid}`,
      }
    }
  }

  return (
    <FormBuilder.Section style={{ width: "80%" }}>
      <FormBuilder.Section
        title={t(`${PAGE_LOCALE_KEY}employeeDetail.detail.subtitle.settlement`)}
        titleVariant="h6">
        <FormBuilder.Row itemsPerRow={3}>
          {FormBuilder.buildFields({
            formProps,
            fields: [
              fieldsConfig.isAutoSettlement,
              fieldsConfig.settlementDate
            ],
          })}
        </FormBuilder.Row>
      </FormBuilder.Section>
      {!isCreate && !isOtherProcessor && (
        <FormBuilder.Section
          title={t(`${PAGE_LOCALE_KEY}employeeDetail.detail.subtitle.keyManagemenet`)}
          titleVariant="h3">
          <ButtonMAS
            variant="primary"
            sx={{ width: "fit-content" }}
            onClick={handleRenewAllinPayKey}
            isLoading={isRenewingKey} // Use the local loading state
          >
            {t(`${PAGE_LOCALE_KEY}employeeDetail.button.allinPay`)}
          </ButtonMAS>
        </FormBuilder.Section>
      )}
      {!isCreate && (
        <FormBuilder.Section
          title={t(`${PAGE_LOCALE_KEY}employeeDetail.detail.subtitle.staticQRCode`)}
          titleVariant="h3">
          <FormBuilder.Row itemsPerRow={1}>
            {FormBuilder.buildFields({
              formProps,
              fields: [fieldsConfig.qrCodeUrl],
            })}
            <QRCodeCanvas
              id="static-qrcode"
              // className={styles.qrcode}
              value={`${config.STATIC_QRCODE}/${employee.uuid}`}
              level="H"
              size={1024}
              style={{ width: 160, height: 160 }}
            />
            <ButtonMAS
              variant="primary"
              isLoading={isDownloadingQrcode}
              onClick={async () => {
                await handleDownloadQrcode();
              }}
            >
              {t(`${PAGE_LOCALE_KEY}employeeDetail.button.download`)}
            </ButtonMAS>
          </FormBuilder.Row>
        </FormBuilder.Section>
      )}
    </FormBuilder.Section >
  );
};

export default React.memo(EmployeeSettlement);
